import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { MessageCircle, Loader2, CheckCircle2, Copy, LogIn, Clock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  nome: "", cognome: "", data_nascita: "", indirizzo: "",
  slot_id: "", cellulare: "", consenso_dati: false,
  accetta_socio: false,
};

export default function Registration() {
  const { t } = useLang();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/slots/public`)
      .then((r) => setSlots(r.data || []))
      .catch((e) => { console.error("Public slots load failed:", e); setSlots([]); })
      .finally(() => setSlotsLoading(false));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    ["nome", "cognome", "data_nascita", "indirizzo", "cellulare"].forEach((k) => {
      if (!form[k]) e[k] = t.form.required;
    });
    if (!form.slot_id) e.slot_id = t.form.required;
    if (!form.consenso_dati) e.consenso_dati = t.form.required;
    if (!form.accetta_socio) e.accetta_socio = t.form.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Compila tutti i campi obbligatori"); return; }
    setLoading(true);
    try {
      const { data: reg } = await axios.post(`${API}/registrations`, form);
      setSuccess({ matricola: reg.matricola, whatsapp_url: reg.whatsapp_url, id: reg.id, chosen_slot: reg.chosen_slot });
      toast.success(`Iscrizione inviata! La tua matricola è ${reg.matricola}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const field = (id, label, type = "text") => (
    <div>
      <label className="block text-sm font-semibold text-[#0F1E3D] mb-1.5" htmlFor={id}>
        {label} <span className="text-[#F97316]">*</span>
      </label>
      <input
        id={id}
        type={type}
        value={form[id]}
        onChange={(e) => set(id, e.target.value)}
        className="warm-input"
        data-testid={`input-${id}`}
      />
      {errors[id] && <p className="text-xs text-[#F97316] mt-1" data-testid={`error-${id}`}>{errors[id]}</p>}
    </div>
  );

  if (success) {
    const copyMat = () => {
      navigator.clipboard?.writeText(success.matricola).then(
        () => toast.success("Matricola copiata!"),
        () => toast.error("Copia non riuscita — scrivila a mano."),
      );
    };
    return (
      <div className="py-14 sm:py-20">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          <div className="card-elevated p-8 sm:p-10 text-center fade-up" data-testid="registration-success">
            <CheckCircle2 size={56} className="mx-auto text-[#10B981]" />
            <h1 className="mt-3 font-display font-extrabold text-2xl sm:text-3xl text-[#0B3D91]">
              Iscrizione inviata!
            </h1>
            <p className="mt-2 text-[#5A6A94]">
              Questa è la tua <strong>matricola personale</strong>. Conservala con cura: sarà il tuo identificativo per accedere e per ogni comunicazione con l'associazione.
            </p>

            <div className="mt-6 rounded-2xl border-2 border-[#F97316] bg-[#FFF7ED] p-6">
              <div className="text-xs font-bold uppercase tracking-widest text-[#F97316]">La tua matricola</div>
              <div className="mt-2 font-mono font-extrabold text-5xl sm:text-6xl text-[#0B3D91] tracking-widest" data-testid="success-matricola">
                {success.matricola}
              </div>
              <button
                type="button"
                onClick={copyMat}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border-2 border-[#DDE4F5] text-[#0B3D91] text-sm font-semibold hover:border-[#0B3D91]"
                data-testid="copy-matricola-btn"
              >
                <Copy size={14} /> Copia matricola
              </button>
            </div>

            <div className="mt-6 text-left rounded-xl bg-[#F6F9FF] border border-[#DDE4F5] p-5 text-sm text-[#0F1E3D] space-y-2">
              {success.chosen_slot && (
                <p className="pb-2 border-b border-[#DDE4F5]"><strong>Orario scelto:</strong> {success.chosen_slot.time}{success.chosen_slot.label ? ` — ${success.chosen_slot.label}` : ""}</p>
              )}
              <p><strong>Prossimi passi:</strong></p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Scrivi a WhatsApp (+39 352 0716656) comunicando la matricola per il pagamento di €30 + €20.</li>
                <li>L'amministrazione approverà l'iscrizione e ti comunicherà la password iniziale.</li>
                <li>Entra in "Accedi" con la tua matricola e password.</li>
              </ol>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              {success.whatsapp_url && (
                <a
                  href={success.whatsapp_url}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-white font-semibold shadow-sm hover:shadow-md transition-all bg-[#25D366] hover:bg-[#1FBB58]"
                  data-testid="whatsapp-cta"
                >
                  <MessageCircle size={16} /> Vai su WhatsApp
                </a>
              )}
              <Link to="/login" className="btn-outline" data-testid="go-to-login">
                <LogIn size={16} /> Vai al login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 sm:py-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 fade-up">
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0B3D91]" data-testid="registration-title">{t.form.title}</h1>
          <p className="mt-2 text-[#5A6A94]">{t.form.subtitle}</p>
          <p className="mt-2 text-xs text-[#F97316] font-semibold">
            💬 Al termine riceverai una matricola di 6 cifre, da usare per il login.
          </p>
        </div>

        <form onSubmit={submit} className="card-elevated p-6 sm:p-8 space-y-6 fade-up" data-testid="registration-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {field("nome", t.form.nome)}
            {field("cognome", t.form.cognome)}
            {field("data_nascita", t.form.dataNascita, "date")}
            {field("cellulare", t.form.cellulare, "tel")}
            <div className="md:col-span-2">{field("indirizzo", t.form.indirizzo)}</div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#0F1E3D] mb-2">
              Scegli l'orario della lezione <span className="text-[#F97316]">*</span>
            </label>
            {slotsLoading ? (
              <div className="text-sm text-[#8494B8]">Caricamento orari...</div>
            ) : slots.length === 0 ? (
              <div className="rounded-xl bg-[#FFF7E6] border border-[#F97316]/30 p-4 text-sm text-[#B45309]">
                ⏳ Non ci sono ancora orari disponibili. Scrivici su WhatsApp <strong>+39 352 0716656</strong> per prenotarti e completare l'iscrizione.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="slot-picker">
                {slots.map((s) => (
                  <label
                    key={s.id}
                    className={`cursor-pointer border-2 rounded-xl px-4 py-3 flex items-center gap-3 transition-all ${
                      form.slot_id === s.id ? "border-[#0B3D91] bg-[#EEF4FF]" : "border-[#DDE4F5] hover:border-[#A9B7DC]"
                    }`}
                    data-testid={`radio-slot-${s.id}`}
                  >
                    <input type="radio" name="slot_id" value={s.id}
                      checked={form.slot_id === s.id}
                      onChange={(e) => set("slot_id", e.target.value)}
                      className="w-4 h-4 accent-[#0B3D91] shrink-0" />
                    <div className="flex-1">
                      <div className="font-display font-bold text-[#0B3D91] text-base flex items-center gap-2">
                        <Clock size={14} /> {s.time}
                      </div>
                      {s.label && <div className="text-xs text-[#5A6A94]">{s.label}</div>}
                    </div>
                  </label>
                ))}
              </div>
            )}
            {errors.slot_id && <p className="text-xs text-[#F97316] mt-1" data-testid="error-slot_id">{errors.slot_id}</p>}
            <p className="text-xs text-[#8494B8] mt-2">Lezioni dal lunedì al venerdì. Potrai richiedere orari extra dopo l'iscrizione.</p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={form.consenso_dati}
              onChange={(e) => set("consenso_dati", e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#0B3D91]" data-testid="input-consenso_dati" />
            <span className="text-sm text-[#5A6A94]">{t.form.consenso}</span>
          </label>
          {errors.consenso_dati && <p className="text-xs text-[#F97316]">{errors.consenso_dati}</p>}

          <div className="rounded-xl border-2 border-[#F97316]/30 bg-[#FFF7ED] p-4 space-y-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.accetta_socio}
                onChange={(e) => set("accetta_socio", e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#F97316]" data-testid="input-accetta_socio" />
              <span className="text-sm text-[#0F1E3D] font-medium">
                Dichiaro di voler diventare <strong>socio/volontario</strong> dell'associazione SS Europa by Amei e di versare la <strong>quota associativa annuale di €20</strong>.
              </span>
            </label>
            <p className="text-xs text-[#8494B8] pl-7">
              💡 La quota socio (€20) è un pagamento <strong>separato</strong> dai €30 del corso. Entrambi si pagano tramite WhatsApp: <strong>+39 352 0716656</strong>
            </p>
            {errors.accetta_socio && <p className="text-xs text-[#F97316] pl-7" data-testid="error-accetta_socio">{errors.accetta_socio}</p>}
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full !py-4 text-base" data-testid="submit-registration-btn">
            {loading ? <><Loader2 className="animate-spin" size={18} /> {t.form.creating}</> : <><MessageCircle size={18} /> Invia iscrizione</>}
          </button>
        </form>
      </div>
    </div>
  );
}
